import React, { useState } from 'react';
import PageTitle from 'components/common/PageTitle';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { RoutePaths } from 'Constants/Common';
import {
    MissedPaymentResponse,
    SuccessfulPaymentNotificationQueueResponse
} from 'api/types/payment';
import { PaymentApi } from 'api/PaymentApi';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';
import WorldPayForm from './worldPay/WorldPayForm';
import { useAppSelector } from 'state/hooks';
import { selectMissedPaymentDetailsByPolicy } from 'state/selectors/vehicles';
import { buildMissedPaymentSuccessPath } from 'helpers/routing';
import { formatCurrency } from 'helpers/currency';

export const PaymentTypes = {
    MissedPayment: 'missed-payment'
} as const;

// Consider in case of different types of payments
// splitting this component into several
// or extracting the logic into hook or something else

const PaymentPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { type: paymentType, id } = useParams();
    const { getAccessTokenSilently } = useAuth0();

    const [missedPaymentResponse, setMissedPaymentResponse] =
        useState<MissedPaymentResponse | null>(null);

    const missedPaymentDetails = useAppSelector((s) =>
        selectMissedPaymentDetailsByPolicy(s, id)
    );
    const totalMissedValue = missedPaymentDetails?.totalMissedValue;

    const amount =
        paymentType === PaymentTypes.MissedPayment
            ? (totalMissedValue?.amount ?? 0)
            : 0;

    const amountToPay = formatCurrency(
        amount,
        totalMissedValue?.currency ?? ''
    );

    if (!amount || paymentType !== PaymentTypes.MissedPayment)
        return <Navigate to={RoutePaths.Home} replace />;

    const missedPaymentRequest = async () => {
        const callback = (data: MissedPaymentResponse) => {
            if (data.redirectUrl) {
                setMissedPaymentResponse(data);
            } else {
                toast.error(
                    t('payment.single_payment.errors.redirect_unavailable')
                );
            }
        };

        const failureCallback = () => {
            toast.error(t('payment.single_payment.errors.request_failed'));
        };

        const api = new PaymentApi();
        const token = await getAccessTokenSilently();

        api.sendOrder(token, id, callback, failureCallback);
    };

    const onPaymentSuccess = async () => {
        if (paymentType === PaymentTypes.MissedPayment) {
            const latestMissedPaymentResponse = missedPaymentResponse;

            if (!latestMissedPaymentResponse?.paymentId || !id) {
                console.warn(
                    'Missing payment context for successful payment notification',
                    {
                        paymentId: latestMissedPaymentResponse?.paymentId,
                        policyNumber: id
                    }
                );
                if (id) {
                    navigate(buildMissedPaymentSuccessPath(id));
                }
                return;
            }

            const api = new PaymentApi();
            const token = await getAccessTokenSilently();
            const request = {
                paymentId: latestMissedPaymentResponse.paymentId,
                policyNumber: id
            };

            api.notifySuccessfulPayment(
                token,
                request,
                (response: SuccessfulPaymentNotificationQueueResponse) => {
                    if (!response.accepted) {
                        console.warn(
                            'Payment notification request was not accepted by the backend',
                            response
                        );
                    }
                },
                () => {
                    console.warn(
                        'Payment notification request failed after successful payment',
                        request
                    );
                }
            );

            navigate(buildMissedPaymentSuccessPath(id));
        }
    };

    const onPaymentPageReady = async () => {
        if (paymentType === PaymentTypes.MissedPayment) {
            await missedPaymentRequest();
            return;
        }

        if (paymentType !== PaymentTypes.MissedPayment) {
            console.debug('This type of payment is not supported');
        }
    };

    const onPaymentCancel = () => {
        navigate(RoutePaths.Home);
    };

    return (
        <div className='page-content'>
            <PageTitle title={t('payment.single_payment.page_title')} />
            <div className='page-content-body p-3 flex-column d-flex gap-3'>
                <div className='card'>
                    <div className='card-body'>
                        <span className='fw-bold'>
                            {t('missed_payment.amount_due')}
                        </span>
                        <span className='ms-2'>{amountToPay}</span>
                    </div>
                </div>
                <WorldPayForm
                    paymentUrl={missedPaymentResponse?.redirectUrl}
                    onPageReady={onPaymentPageReady}
                    onPaymentSuccess={onPaymentSuccess}
                    onPaymentCancel={onPaymentCancel}
                />
            </div>
        </div>
    );
};

export default PaymentPage;
